import { createContext, useContext, useEffect, type ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { NodeStateResponse } from '@khc/contracts';
import type { LiveObservation } from '../data/live';
import { useHubChannels, type HubChannels } from '../data/channels';
import { setHubWriteGuard } from '../data/writeGuard';
import { Banner, Button } from '../ui';
import { zh } from '../i18n/zh';

interface HubChannelsContextValue {
  hubId: string;
  stateQuery: UseQueryResult<NodeStateResponse, Error>;
  live: LiveObservation;
  channels: HubChannels;
}

const HubChannelsContext = createContext<HubChannelsContextValue | null>(null);

export function HubChannelsProvider({
  hubId,
  liveEndpoint,
  children,
}: {
  hubId: string;
  liveEndpoint: string | null | undefined;
  children: ReactNode;
}) {
  const value = useHubChannels(hubId, liveEndpoint);
  const { canApply, presence, control } = value.channels;

  useEffect(() => {
    setHubWriteGuard(
      control.reachable === false
        ? {
            hubId,
            canMutate: false,
            reason: presence === 'live-only' ? zh.channels.liveOnlyDesc : zh.channels.controlFailed,
          }
        : null,
    );
    return () => setHubWriteGuard(null);
  }, [canApply, control.reachable, hubId, presence]);

  return <HubChannelsContext.Provider value={{ hubId, ...value }}>{children}</HubChannelsContext.Provider>;
}

export function useHubChannelsContext(): HubChannelsContextValue {
  const value = useContext(HubChannelsContext);
  if (!value) throw new Error('useHubChannelsContext 必须在 HubChannelsProvider 内使用');
  return value;
}

/** Hub 工作区顶部的只读提示；只在控制面明确不可达时出现，避免首屏探测误报。 */
export function HubReadOnlyNotice() {
  const { channels, stateQuery } = useHubChannelsContext();
  if (channels.control.reachable !== false) return null;
  return (
    <Banner
      tone={channels.presence === 'live-only' ? 'warn' : 'danger'}
      title={channels.presence === 'live-only' ? zh.channels.liveOnlyTitle : zh.channels.controlUnavailableTitle}
      description={
        channels.presence === 'live-only'
          ? zh.channels.liveOnlyDesc
          : zh.channels.controlUnavailableDesc
      }
      action={
        <Button
          label={zh.channels.retryControl}
          size="sm"
          loading={stateQuery.isFetching}
          onClick={() => void stateQuery.refetch()}
        />
      }
    />
  );
}
